
import * as React from "react";
import type { Initiative, Person, Status } from "@/lib/types";
import { TEAMS, tagPickerOptions, pruneTagsForTeam, tagNum, QUARTERS, STATUS_LABELS, STATUS_KEYS, DESCRIPTION_MAX, DESCRIPTION_COUNTER_AT, DESCRIPTION_PLACEHOLDER, quarterForDate, quarterLabel, localISO } from "@/lib/constants";
import { DriPicker } from "./dri-picker";
import { DescriptionCounter } from "./primitives";
import { loadDefaults, resolveDefaults, saveDefaults } from "@/lib/form-defaults";
import { workstreamNames } from "@/lib/group-by";

export function NewInitiativeModal({
  open,
  onClose,
  onCreate,
  roster,
  onPickPerson,
  currentUser = null,
  initiatives = [],
}: {
  open: boolean;
  /** Signed-in user (null when auth is off) — the owner fallback and the key
   *  the remembered team/pod/owner are stored under. */
  currentUser?: Person | null;
  /** Active initiatives — to infer a first-time user's usual team and pod. */
  initiatives?: Initiative[];
  onClose: () => void;
  onCreate: (data: Omit<Initiative, "id" | "sortOrder">) => void;
  roster: Person[];
  /** Directory pick → roster row (the store's create-or-reuse addPerson). */
  onPickPerson: (name: string, email: string) => Person;
}) {
  const [briefUrl, setBriefUrl] = React.useState("");
  const [designUrl, setDesignUrl] = React.useState("");

  const [name, setName] = React.useState("");
  const [tldr, setTldr] = React.useState(""); // required — the one-or-two-sentence description
  const [jira, setJira] = React.useState("");
  const [teamId, setTeamId] = React.useState("");
  const [podId, setPodId] = React.useState("");
  const [driId, setDriId] = React.useState("");
  const [pillarIds, setPillarIds] = React.useState<string[]>([]);
  const [targetDate, setTargetDate] = React.useState(""); // required — quarter derives from it
  const [startDate, setStartDate] = React.useState(""); // optional — planned start (actual start if born in-progress)
  const [status, setStatus] = React.useState<Status>("not-started");
  const [workstream, setWorkstream] = React.useState(""); // optional — groups related work across teams

  React.useEffect(() => {
    if (!open) {
      setBriefUrl("");
      setDesignUrl("");
      setName("");
      setTldr("");
      setJira(""); setTeamId(""); setPodId(""); setDriId("");
      setPillarIds([]); setTargetDate(""); setStartDate(""); setStatus("not-started"); setWorkstream("");
    }
  }, [open]);

  // Start each new initiative from the last team/pod/owner this user picked
  // (see lib/form-defaults.ts). Re-resolved if the roster or signed-in user
  // arrives after the form opened — but never once the user has touched
  // team, pod or owner.
  const pickedRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    pickedRef.current = false;
    // Most work is planned from today; the field stays editable and clearable.
    setStartDate(localISO(new Date()));
  }, [open]);
  React.useEffect(() => {
    if (!open || pickedRef.current) return;
    const d = resolveDefaults({ remembered: loadDefaults(currentUser?.id), currentUserId: currentUser?.id, initiatives, roster });
    setTeamId(d.teamId);
    setPodId(d.podId);
    setDriId(d.driId);
    // initiatives only refines a first-time guess; re-resolving on every data
    // change would churn while the form is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roster, currentUser?.id]);

  React.useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);

  const team = TEAMS.find(t => t.id === teamId);
  const pods = team?.pods ?? [];
  const podRequired = pods.length > 0; // pod-less teams (UX Research) create with no pod

  const togglePillar = (id: string) => {
    setPillarIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  const onTeamChange = (val: string) => {
    pickedRef.current = true;
    setTeamId(val);
    setPodId(""); // reset pod when team changes
    setPillarIds(cur => pruneTagsForTeam(val, "", cur)); // tags are scoped by team/pod
  };

  const onPodChange = (val: string) => {
    pickedRef.current = true;
    setPodId(val);
    setPillarIds(cur => pruneTagsForTeam(teamId, val, cur));
  };

  const tagOptions = tagPickerOptions(teamId, podId, pillarIds);

  // Quarter is derived from the (required) target date.
  const inferredQ = (() => {
    if (!targetDate) return "";
    const d = new Date(targetDate + "T00:00:00");
    return quarterForDate(d) ?? "";
  })();

  const startAfterTarget = !!(startDate && targetDate && startDate > targetDate);

  // Moving the start onto or past the target pushes the target to the day
  // after it, so the two dates never cross. An empty target stays empty.
  const onStartChange = (val: string) => {
    setStartDate(val);
    if (val && targetDate && targetDate <= val) {
      const d = new Date(val + "T00:00:00");
      d.setDate(d.getDate() + 1);
      setTargetDate(localISO(d));
    }
  };

  const canCreate =
    name.trim() &&
    tldr.trim() && // required going forward; historical rows may still be empty
    teamId &&
    (podId || !podRequired) &&
    driId &&
    targetDate && // tags are optional
    !!inferredQ && // target must land inside a defined fiscal year
    !startAfterTarget;

  const submit = () => {
    if (!canCreate) return;
    const d = new Date(targetDate + "T00:00:00");
    const target: [number, number, number] = [d.getFullYear(), d.getMonth(), d.getDate()];
    const s = startDate ? new Date(startDate + "T00:00:00") : undefined;
    saveDefaults(currentUser?.id, { teamId, podId, driId });
    onCreate({
      name: name.trim(),
      tldr: tldr.trim(),
      teamId,
      podId,
      driId,
      pillarIds,
      // canCreate guarantees inferredQ — no silent misfiling fallback.
      targetQ: inferredQ,
      target,
      start: s ? [s.getFullYear(), s.getMonth(), s.getDate()] : undefined,
      status,
      notion: briefUrl.trim() || undefined,
      design: designUrl.trim() || undefined,
      jira: jira.trim() || undefined,
      workstream: workstream.trim() || undefined,
    });
  };

  if (!open) return null;

  return (
    <div className={`modal-backdrop ${open ? "open" : ""}`} onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-init-title"
      >
        <div className="modal-header">
          <h2 id="new-init-title">New initiative</h2>
        </div>

        <div className="modal-body">
          <div className="field">
            <label htmlFor="new-init-name">Initiative name <span className="req">*</span></label>
            <input
              id="new-init-name"
              type="text"
              placeholder="e.g. Pre-Move-In Inspection Hub"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="new-init-description">Description <span className="req">*</span></label>
            <textarea
              id="new-init-description"
              className="description-input"
              rows={2}
              maxLength={DESCRIPTION_MAX}
              placeholder={DESCRIPTION_PLACEHOLDER}
              value={tldr}
              onChange={e => setTldr(e.target.value)}
            />
            <span className={`hint ${tldr.length >= DESCRIPTION_COUNTER_AT ? "has-counter" : ""}`}>
              <span>One or two sentences. Written for someone seeing this initiative for the first time.</span>
              {tldr.length >= DESCRIPTION_COUNTER_AT && <DescriptionCounter length={tldr.length} />}
            </span>
          </div>

          <div className="field">
            <label htmlFor="new-init-brief">Brief URL (optional)</label>
            <input
              id="new-init-brief"
              type="url"
              placeholder="https://… link to the product brief"
              value={briefUrl}
              onChange={e => setBriefUrl(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-init-design">Design / prototype URL (optional)</label>
            <input
              id="new-init-design"
              type="url"
              placeholder="https://… link to the designs or a prototype"
              value={designUrl}
              onChange={e => setDesignUrl(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-init-jira">Jira epic URL or key (optional)</label>
            <input id="new-init-jira" type="text" placeholder="ROAD-2900" value={jira} onChange={e => setJira(e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="field">
              <label htmlFor="new-init-team">Team <span className="req">*</span></label>
              <select id="new-init-team" value={teamId} onChange={e => onTeamChange(e.target.value)}>
                <option value="" disabled>Select…</option>
                {TEAMS.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="new-init-pod">Pod {(podRequired || !teamId) && <span className="req">*</span>}</label>
              <select id="new-init-pod" value={podId} onChange={e => onPodChange(e.target.value)} disabled={!teamId || !podRequired}>
                <option value="" disabled>{!teamId ? "Pick a team first" : podRequired ? "Select…" : "No pods on this team"}</option>
                {pods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label htmlFor="new-init-dri">Owner (DRI) <span className="req">*</span></label>
            <DriPicker
              inputId="new-init-dri"
              roster={roster}
              value={driId}
              onChange={id => { pickedRef.current = true; setDriId(id); }}
              onPickPerson={onPickPerson}
            />
          </div>

          <div className="field">
            <label>Tags <span style={{ fontStyle: "italic", textTransform: "none", letterSpacing: 0, fontFamily: "var(--font-source-serif), serif", color: "var(--muted-ink)", fontWeight: 400 }}>(optional — pick any that apply)</span></label>
            {tagOptions.length === 0 && (
              <span style={{ color: "var(--muted-2)", fontStyle: "italic", fontSize: 12.5 }}>
                {teamId ? "No tags for this team." : "Pick a team to see its tags."}
              </span>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {tagOptions.map(p => {
                const on = pillarIds.includes(p.id);
                return (
                  <label
                    key={p.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 10px",
                      border: `1px solid ${on ? "var(--cobalt)" : "var(--hairline)"}`,
                      borderRadius: 6,
                      background: on ? "var(--cobalt-soft)" : "var(--surface)",
                      cursor: "pointer",
                      fontSize: 12.5,
                      transition: "all 120ms",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => togglePillar(p.id)}
                      style={{ margin: 0 }}
                    />
                    {tagNum(p) && <span className="pillar-chip-num">{tagNum(p)}</span>}
                    <span style={{ color: "var(--ink-2)", flex: 1, lineHeight: 1.3 }}>{p.short}</span>
                  </label>
                );
              })}
            </div>
            <span className="hint">The goals this initiative serves.</span>
          </div>

          <div className="field">
            <label htmlFor="new-init-workstream">Workstream <span className="u-muted-italic-serif" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
            <input
              id="new-init-workstream"
              type="text"
              list="new-init-workstreams"
              maxLength={80}
              value={workstream}
              onChange={e => setWorkstream(e.target.value)}
              placeholder="Test automation"
            />
            <datalist id="new-init-workstreams">
              {workstreamNames(initiatives).map(w => <option key={w} value={w} />)}
            </datalist>
            <span className="hint">The effort this is part of, like Test automation. Can span teams and pods.</span>
          </div>

          <div className="field">
            <label htmlFor="new-init-status">Status</label>
            <select id="new-init-status" value={status} onChange={e => setStatus(e.target.value as Status)}>
              {STATUS_KEYS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="field">
              <label htmlFor="new-init-start">Start date <span className="u-muted-italic-serif" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
              <input id="new-init-start" type="date" value={startDate} onChange={e => onStartChange(e.target.value)} />
              <span className="hint">
                {startAfterTarget
                  ? <span style={{ color: "var(--error-ink)" }}>Start is after the target date.</span>
                  : "When work is planned to begin — movable until it's in progress."}
              </span>
            </div>
            <div className="field">
              <label htmlFor="new-init-target">Target delivery date <span className="req">*</span></label>
              <input id="new-init-target" type="date" value={targetDate} min={startDate || undefined} onChange={e => setTargetDate(e.target.value)} />
              <span className="hint">
                {targetDate
                  ? inferredQ
                    ? <>Falls in <strong style={{ color: "var(--ink)" }}>{quarterLabel(inferredQ)}</strong> · {QUARTERS.find(x => x.id === inferredQ)?.months.join(", ")}</>
                    : <span style={{ color: "var(--error-ink)" }}>Outside the {QUARTERS[0].year}–{QUARTERS[QUARTERS.length - 1].year} window.</span>
                  : "Quarter is inferred from the date you pick. You can re-plan later — the original commitment is tracked once work starts."}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!canCreate}>
            Create initiative
          </button>
        </div>
      </div>
    </div>
  );
}
